import * as XLSX from 'xlsx';
import { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
// Hooks y servicios
import { useScaleRequirementsForEdit } from '../hooks/queries'; // ✅ Importar hook
import { importScaleRequirements, previewScaleRequirements } from '../services/baremos';

export default function ScaleImportModal({ 
  visible, 
  onHide, 
  clientId, 
  medicalCenterId, 
  onSuccess 
  
}) {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [activeStep, setActiveStep] = useState('upload');
  const [showAllErrors, setShowAllErrors] = useState(false); 
  const toast = useRef(null);
  const fileUploadRef = useRef(null);

  // ✅ Cargar datos del baremo para obtener el scale_name real
  const requirementsQuery = useScaleRequirementsForEdit({
    clientId: Number(clientId),
    medicalCenter: medicalCenterId,
    options: { enabled: visible && !!medicalCenterId }
  });

  // ✅ Obtener el nombre del baremo del sistema
  const expectedScaleName = (() => {
    if (!requirementsQuery.data || requirementsQuery.data.length === 0) return null;
    const valid = requirementsQuery.data.find(item => item.scale_name);
    return valid?.scale_name || null;
  })();

  const handleFileSelect = async (e) => {
    if (e.files && e.files.length > 0) {
      const selectedFile = e.files[0];

      setFile(selectedFile);

      // ✅ Validar antes de previsualizar
      await handlePreview(selectedFile);
    }
  };

const handlePreview = async (fileToPreview) => {
  if (!fileToPreview) return;

  setIsLoading(true);
  try {
    const data = await fileToPreview.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });


    // ✅ 1. Filtrar filas vacías
    const cleanedData = jsonData.filter((row) =>
      Array.isArray(row) &&
      row.some((cell) => cell != null && cell !== '' && String(cell).trim() !== '')
    );

    if (cleanedData.length === 0) {
      setPreviewData({
        total: 0,
        updated: 0,
        changes: [],
        errors: ['El archivo está vacío o no contiene datos válidos.']
      });
      setActiveStep('preview');
      return;
    }

    const headers = cleanedData[0];
    const headerMap = headers.reduce((acc, header, index) => {
      acc[header?.toString().trim().toUpperCase()] = index;
      return acc;
    }, {});

    // ✅ 2. Validar columnas obligatorias
    const requiredColumns = ['CODIGO', 'PRECIO', 'NOMBRE_DEL_BAREMO', 'TIPO_SERVICIO', 'CODIGO_SERVICIO', 'SERVICIO'];
    const missingColumns = requiredColumns.filter((col) => headerMap[col] === undefined);

    if (missingColumns.length > 0) {
      setPreviewData({
        total: 0,
        updated: 0,
        changes: [],
        errors: [
          `Faltan columnas obligatorias: ${missingColumns.join(', ')}.`,
          `Se requieren: ${requiredColumns.join(', ')}.`,
          'Verifique que los nombres sean exactos (en mayúsculas, sin tildes ni espacios extra).'
        ]
      });
      setActiveStep('preview');
      return;
    }

    // ✅ 3. Procesar filas: validar y filtrar
    const validRows = [];
    const rowErrors = [];

    cleanedData.slice(1).forEach((row, index) => {
      const rowIndex = index + 2; // Fila en Excel (1-indexed)
      const codigo = row[headerMap['CODIGO']];
      const precio = row[headerMap['PRECIO']];
      const nombreBaremo = row[headerMap['NOMBRE_DEL_BAREMO']]?.toString().trim();
      const tipoServicio = row[headerMap['TIPO_SERVICIO']]?.toString().trim();
      const codigoServicio = row[headerMap['CODIGO_SERVICIO']]?.toString().trim();
      const nombreServicio = row[headerMap['SERVICIO']]?.toString().trim();

      let hasError = false;

      // Validar CODIGO
      if (!codigo || String(codigo).trim() === '') {
        rowErrors.push(`Fila ${rowIndex}: CODIGO está vacío.`);
        hasError = true;
      }

      // Validar PRECIO
      const precioNum = parseFloat(precio);
      if (!precio || isNaN(precioNum) || precioNum < 0) {
        rowErrors.push(`Fila ${rowIndex}: PRECIO no es un número válido.`);
        hasError = true;
      }

      // Validar NOMBRE_DEL_BAREMO
      if (!nombreBaremo) {
        rowErrors.push(`Fila ${rowIndex}: NOMBRE_DEL_BAREMO está vacío.`);
        hasError = true;
      } else if (expectedScaleName && nombreBaremo.toUpperCase() !== expectedScaleName.toUpperCase()) {
        rowErrors.push(
          `Fila ${rowIndex}: Nombre del baremo incorrecto. Esperado: "${expectedScaleName}", Obtenido: "${nombreBaremo}"`
        );
        hasError = true;
      }

      if (!tipoServicio) {
        rowErrors.push(`Fila ${rowIndex}: TIPO_SERVICIO está vacío.`);
        hasError = true;
      }

      if (!codigoServicio) {
        rowErrors.push(`Fila ${rowIndex}: CODIGO_SERVICIO está vacío.`);
        hasError = true;
      }

      if (!nombreServicio) {
        rowErrors.push(`Fila ${rowIndex}: SERVICIO está vacío.`);
        hasError = true;
      }

      // ✅ Solo agregar si no tuvo errores
      if (!hasError) {
        validRows.push(row);
      }
    });

    // ✅ 4. Si no hay ni una fila válida → error
    if (validRows.length === 0) {
      setPreviewData({
        total: 0,
        updated: 0,
        changes: [],
        errors: [
          'No se encontraron filas válidas en el archivo.',
          ...(rowErrors.length > 0
            ? [`Errores detectados (${rowErrors.length}):`, ...rowErrors.slice(0, 10)]
            : []),
          rowErrors.length > 10 && `... y ${rowErrors.length - 10} más.`
        ].filter(Boolean)
      });
      setActiveStep('preview');
      return;
    }

    // ✅ 5. Construir archivo limpio con solo filas válidas
    const finalData = [headers, ...validRows];
    const cleanedWorksheet = XLSX.utils.aoa_to_sheet(finalData);
    const cleanedWorkbook = {
      Sheets: { [workbook.SheetNames[0]]: cleanedWorksheet },
      SheetNames: [workbook.SheetNames[0]]
    };
    const cleanedFileBuffer = XLSX.write(cleanedWorkbook, { bookType: 'xlsx', type: 'array' });
    const cleanedFile = new File([cleanedFileBuffer], fileToPreview.name, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // ✅ 6. Enviar al backend el archivo limpio
    const formData = new FormData();
    formData.append('file', cleanedFile);
    formData.append('client', clientId);
    if (medicalCenterId) formData.append('medical_center', medicalCenterId);

    const result = await previewScaleRequirements(clientId, formData);

    // ✅ 7. Combinar errores (validación + backend)
    const allErrors = [...rowErrors, ...(result.errors || [])];

    setPreviewData({
      ...result,
      errors: allErrors.length > 0 ? allErrors : null
    });

    setActiveStep('preview');

    // ✅ 8. Mostrar advertencia si se descartaron filas
    if (rowErrors.length > 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Advertencia',
        detail: `Se ignoraron ${rowErrors.length} filas con errores. Se procesarán ${validRows.length} filas válidas.`,
        life: 6000
      });
    } else {
      toast.current.show({
        severity: 'info',
        summary: 'Previsualización lista',
        detail: `Analizados ${result.total} registros. ${
          result.updated ? `Se actualizarán ${result.updated}` : 'No hay cambios.'
        }`,
        life: 4000
      });
    }
  } catch (error) {
    console.error('Error en handlePreview:', error);
    toast.current.show({
      severity: 'error',
      summary: 'Error en previsualización',
      detail: error.message || 'No se pudo procesar el archivo.',
      life: 8000
    });
    setActiveStep('upload');
  } finally {
    setIsLoading(false);
  }
};

const handleImport = async () => {
  if (!file) return;

  setIsLoading(true);
  try {
    // ✅ Repetir el proceso de limpieza
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    const cleanedData = jsonData.filter(row =>
      row.some(cell => cell != null && cell !== '' && String(cell).trim() !== '')
    );

    const cleanedWorksheet = XLSX.utils.aoa_to_sheet(cleanedData);
    const cleanedWorkbook = { 
      Sheets: { [workbook.SheetNames[0]]: cleanedWorksheet }, 
      SheetNames: [workbook.SheetNames[0]] 
    };
    const cleanedFileBuffer = XLSX.write(cleanedWorkbook, { bookType: 'xlsx', type: 'array' });
    const cleanedFile = new File([cleanedFileBuffer], file.name, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // ✅ Enviar el archivo limpio
    const formData = new FormData();
    formData.append('file', cleanedFile); // ← No usar `file`, usar `cleanedFile`
    formData.append('client', clientId);
    if (medicalCenterId) formData.append('medical_center', medicalCenterId);

    const result = await importScaleRequirements(clientId, formData);
    setImportResults(result);
    setActiveStep('results');

    toast.current.show({
      severity: 'success',
      summary: 'Importación exitosa',
      detail: `Actualizados: ${result.updated}`,
      life: 5000,
    });
  } catch (error) {
    toast.current.show({
      severity: 'error',
      summary: 'Error al importar',
      detail: error.message,
      life: 5000,
    });
  } finally {
    setIsLoading(false);
  }
};

  const handleCancelPreview = () => {
    setFile(null);
    setPreviewData(null);
    setActiveStep('upload');
    if (fileUploadRef.current) fileUploadRef.current.clear();
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData(null);
    setImportResults(null);
    setActiveStep('upload');
    setShowAllErrors(false);
    if (fileUploadRef.current) fileUploadRef.current.clear();
    onHide();
    onSuccess();
  };

  const renderUploadStep = () => (
    <div className="p-fluid">
      <div className="field mt-3">
        <Card title="Instrucciones" className="text-sm">
          <p>El archivo debe contener estas columnas:</p>
          <ul className="mt-0">
            <li><strong>CODIGO</strong>: Código del baremo</li>
            <li><strong>NOMBRE_DEL_BAREMO</strong>: Nombre del baremo (obligatorio)</li>
            <li><strong>TIPO_SERVICIO</strong>: Tipo de servicio</li>
            <li><strong>CODIGO_SERVICIO</strong> o <strong>SERVICIO</strong></li>
            <li><strong>SERVICIO</strong>: Nombre del servicio</li>
            <li><strong>PRECIO</strong>: Precio (decimal con punto)</li>
          </ul>
        </Card>
      </div>
      {/* (El resto del render permanece igual) */}
      <div className="field">
        <label htmlFor="importFile">Seleccionar archivo Excel (XLSX)</label>
        <FileUpload
          key={activeStep} 
          ref={fileUploadRef}
          name="file"
          accept=".xlsx,.csv"
          maxFileSize={1000000} // 1MB
          onSelect={handleFileSelect}
          onRemove={() => {
            setFile(null);
            setPreviewData(null);
            setActiveStep('upload');
            if (fileUploadRef.current) fileUploadRef.current.clear();
          }}
          customUpload
          mode="advanced"
          auto={false}
          multiple={false}
          disabled={isLoading || requirementsQuery.isLoading}
          uploadOptions={{ style: { display: 'none' } }}
          cancelOptions={{ style: { display: 'none' } }}
        />
      </div>

      {/* ✅ Mostrar mensaje de carga */}
      {requirementsQuery.isLoading && (
        <div className="field">
          <Card>
            <i className="pi pi-spin pi-spinner mr-2"></i>
            <span>Cargando información del baremo...</span>
          </Card>
        </div>
      )}

      
      
    </div>
  );

 
  const renderPreviewStep = () => {
    if (!previewData) return null;
    const summaryCard = (
      <div className="grid mb-3">
        <div className="col-6">
          <Card title="Total" className="text-center">
            <h2 className="m-0">{previewData.total}</h2>
          </Card>
        </div>
        <div className="col-6">
          <Card title="Actualizaciones" className="text-center">
            <h2 className="m-0 text-blue-500">{previewData.updated}</h2>
          </Card>
        </div>
      </div>
    );
    return (
      <div>
        <h5>Revisión de Cambios Antes de Importar</h5>
        {summaryCard}
        {/* === AQUÍ VA EL MENSAJE DE ERRORES === */}
      {previewData.errors && previewData.errors.length > 0 && (
        <Message 
          severity="error" 
          text={
            <div>
              <p><strong>{previewData.errors.length} errores encontrados:</strong></p>
              <ul style={{ margin: 0, paddingLeft: '1.2em', marginBottom: '0.5rem' }}>
                {previewData.errors.slice(0, 5).map((err, i) => (
                  <li key={i} style={{ marginBottom: '0.3rem' }}>{err}</li>
                ))}
                {previewData.errors.length > 5 && !showAllErrors && (
                  <li>
                    <Button
                      label={`Ver ${previewData.errors.length - 5} más`}
                      className="p-button-text p-button-sm"
                      onClick={() => setShowAllErrors(true)}
                      type="button"
                    />
                  </li>
                )}
              </ul>
              {showAllErrors && (
                <ul style={{ margin: 0, paddingLeft: '1.2em', marginTop: '0.5rem' }}>
                  {previewData.errors.slice(5).map((err, i) => (
                    <li key={i} style={{ marginBottom: '0.3rem' }}>{err}</li>
                  ))}
                  <li>
                    <Button
                      label="Ver menos"
                      className="p-button-text p-button-sm"
                      onClick={() => setShowAllErrors(false)}
                      type="button"
                    />
                  </li>
                </ul>
              )}
            </div>
          }
          style={{ marginBottom: '1rem' }}
        />
      )}
      {/* ===================================== */}
        <DataTable 
          value={previewData.changes.filter(item => item.type === 'updated')} 
          paginator 
          rows={5}
          emptyMessage="No se detectaron cambios"
          className="mt-3"
        >
          <Column field="row" header="Fila" style={{ width: '70px' }} />
          <Column 
            header="Estado" 
            body={(rowData) => (
              <Badge 
                value={rowData.type === 'updated' ? 'Actualizado' : 'Sin cambios'} 
                severity={rowData.type === 'updated' ? 'info' : 'success'} 
              />
            )}
            style={{ width: '120px' }}
          />
          <Column 
            header="Servicio" 
            field="data.requirement" 
            style={{ minWidth: '200px' }}
          />
          <Column 
            header="Tipo" 
            field="data.requirement__type_requirement" 
            style={{ minWidth: '180px' }}
          />
          <Column 
            header="Cambios de Precio" 
            body={(rowData) => (
              rowData.type === 'updated' && rowData.changes.price ? (
                <div className="flex align-items-center gap-2">
                  <span className="text-red-500 ">
                    {rowData.changes.price.old.toFixed(2)}
                  </span>
                  <i className="pi pi-arrow-right text-sm" />
                  <span className="text-green-500 font-semibold">
                    {rowData.changes.price.new}
                  </span>
                </div>
              ) : '-'
            )}
            style={{ width: '200px' }}
          />
        </DataTable>
        <div className="flex justify-content-between mt-4">
          <Button
            label="Editar Archivo"
            icon="pi pi-times"
            onClick={handleCancelPreview}
            disabled={isLoading}
            severity="secondary"
          />
          {previewData.updated > 0 ? (
            <Button
              label="Confirmar e Importar"
              icon="pi pi-check"
              disabled={
                isLoading || 
                !previewData || 
                (previewData.errors && previewData.errors.length > 0)
              }
              onClick={handleImport}
              loading={isLoading}
              severity="success"
            />
          ) : (
            <Message
              severity="info"
              text="No se detectaron cambios en los precios."
              style={{ marginTop: '1rem' }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderResultsStep = () => {
    if (!importResults) return null;
    return (
      <div>
        <div className="grid mb-3">
          <div className="col-6">
            <Card title="Total" className="text-center"><h2>{importResults.total}</h2></Card>
          </div>
          <div className="col-6">
            <Card title="Actualizados" className="text-center"><h2 className="text-blue-500">{importResults.updated}</h2></Card>
          </div>
        </div>
        <DataTable 
          value={importResults.changes.filter(item => item.type === 'updated')} 
          paginator 
          rows={5}
          className="mt-3"
          rowClassName={(rowData) => rowData.type === 'updated' ? 'bg-blue-50' : undefined}
        >
          <Column field="row" header="Fila" style={{ width: '70px' }} />
          <Column 
            header="Estado" 
            body={(rowData) => (
              <Badge 
                value={rowData.type === 'updated' ? 'Actualizado' : 'Sin cambios'} 
                severity={rowData.type === 'updated' ? 'info' : 'success'} 
              />
            )}
            style={{ width: '120px' }}
          />
          <Column 
            header="Servicio" 
            field="data.requirement" 
            style={{ minWidth: '200px' }}
          />
          <Column 
            header="Precio Final" 
            field="data.price" 
            style={{ width: '120px' }}
            body={(rowData) => (
              <span className={rowData.type === 'updated' ? 'font-semibold text-blue-600' : ''}>
                {rowData.data.price}
              </span>
            )}
          />
          <Column 
            header="Detalle Cambios" 
            body={(rowData) => (
              rowData.type === 'updated' ? (
                <div className="flex flex-column">
                  {Object.entries(rowData.changes).map(([field, change]) => (
                    <div key={field} className="mb-1">
                      <span className="capitalize">{field}: </span>
                      <span className="text-red-500 line-through mr-2">
                        {typeof change.old === 'number' ? change.old.toFixed(2) : change.old}
                      </span>
                      <span className="text-green-500">
                        {change.new}
                      </span>
                    </div>
                  ))}
                </div>
              ) : '-'
            )}
          />
        </DataTable>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Dialog 
        header={
          activeStep === 'upload' ? "Importar Baremo" :
          activeStep === 'preview' ? "Previsualizar Cambios" :
          "Resultados de Importación"
        }
        visible={visible} 
        style={{ width: '70vw' }}
        onHide={handleClose}
      >
        {activeStep === 'upload' && renderUploadStep()}
        {activeStep === 'preview' && renderPreviewStep()}
        {activeStep === 'results' && renderResultsStep()}
      </Dialog>
    </>
  );
}